import * as React from "react";
import { Link, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";
import { buildPathnameForRegion, getRegionFromBranchId, Region } from "utils/regionUtils";

interface PathParams {
  lltsBranchId?: string;
}

const getRegionNameKey = (region: Region): string =>
  region === Region.UK ? "appHeader.region.ukName" : "appHeader.region.usName";

interface Props {
  /**
   * Translation key for the notice text. The key must accept a `{{currentRegion}}`
   * interpolation parameter. Defaults to `regionNotice.text`.
   */
  messageKey?: string;
}

/**
 * Renders a notice paragraph informing the user which regional data center their submission
 * will be hosted in, with a link to switch the current page to the other supported region
 * (US/UK). The path, search and hash of the current location are preserved across the switch.
 * The link triggers a full page reload so the app re-initializes against the target region.
 */
const RegionNotice: React.FC<Props> = ({ messageKey = "regionNotice.text" }) => {
  const { t } = useTranslation();
  const { lltsBranchId } = useParams<PathParams>();
  const location = useLocation();

  const currentRegion = getRegionFromBranchId(lltsBranchId);
  const otherRegion = currentRegion === Region.UK ? Region.US : Region.UK;

  const nextPathname = buildPathnameForRegion(location.pathname, lltsBranchId, otherRegion);
  const switchHref = `${nextPathname}${location.search}${location.hash}`;

  const currentRegionName = t(getRegionNameKey(currentRegion));
  const otherRegionName = t(getRegionNameKey(otherRegion));

  const onSwitchRegion = React.useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      // Force a full page reload (bypassing React Router) so the app re-initializes
      // against the target region's API base URL and locale.
      event.preventDefault();
      window.location.assign(switchHref);
    },
    [switchHref]
  );

  return (
    <Typography variant="body2" sx={{ mt: 2, fontStyle: "italic" }} data-testid="region-notice">
      {t(messageKey, { currentRegion: currentRegionName })}{" "}
      <Link href={switchHref} onClick={onSwitchRegion} sx={{ verticalAlign: "baseline" }}>
        {t("regionNotice.switchLink", { otherRegion: otherRegionName })}
      </Link>
    </Typography>
  );
};

export { RegionNotice };
